import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Trash2, Check, Eye, Video, Image, 
  Building2, RefreshCw, Plus, X, Save, Loader2,
  FileText, Upload, Download, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Toaster } from './ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminConsole() {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [capabilityDeck, setCapabilityDeck] = useState(null);
  const [loading, setLoading] = useState(true);

  // Video form state
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    video_url: '',
    platform: 'youtube',
    tech_stack: [],
    order: 0,
  });
  const [techInput, setTechInput] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const videoFileRef = useRef(null);
  const videoThumbnailRef = useRef(null);

  // Diagram form state
  const [showDiagramForm, setShowDiagramForm] = useState(false);
  const [diagramForm, setDiagramForm] = useState({
    title: '',
    description: '',
    category: 'ai_platform',
    components: [],
    image_data: '',
  });
  const [componentInput, setComponentInput] = useState('');
  const [diagramUploading, setDiagramUploading] = useState(false);
  const diagramFileRef = useRef(null);

  // Capability deck state
  const [deckUploading, setDeckUploading] = useState(false);
  const deckFileRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, contactsRes, videosRes, diagramsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/diagrams`),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setVideos(videosRes.data);
      setDiagrams(diagramsRes.data);

      // Try to fetch capability deck
      try {
        const deckRes = await axios.get(`${API}/capability-deck`);
        setCapabilityDeck(deckRes.data);
      } catch {
        setCapabilityDeck(null);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Contact handlers
  const markAsRead = async (contactId) => {
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}/read`);
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, read: true } : c));
      setStats(prev => prev ? { ...prev, unread_contacts: prev.unread_contacts - 1 } : prev);
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      setStats(prev => prev ? { ...prev, contacts: prev.contacts - 1 } : prev);
      toast.success('Contact deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Video handlers
  const handleVideoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video file must be under 100MB');
      return;
    }

    setVideoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        setVideoForm(prev => ({ 
          ...prev, 
          video_url: '',
          platform: 'uploaded'
        }));
        // Store base64 data temporarily
        videoFileRef.current = reader.result;
        toast.success('Video loaded');
        setVideoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Failed to load video');
      setVideoUploading(false);
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      videoThumbnailRef.current = reader.result;
      toast.success('Thumbnail loaded');
    };
    reader.readAsDataURL(file);
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setVideoUploading(true);
    try {
      const payload = {
        ...videoForm,
        video_data: videoFileRef.current || '',
        thumbnail_data: videoThumbnailRef.current || '',
      };
      const response = await axios.post(`${API}/admin/videos`, payload);
      setVideos(prev => [...prev, response.data]);
      setShowVideoForm(false);
      setVideoForm({
        title: '',
        description: '',
        video_url: '',
        platform: 'youtube',
        tech_stack: [],
        order: 0,
      });
      videoFileRef.current = null;
      videoThumbnailRef.current = null;
      toast.success('Video added successfully');
    } catch (error) {
      toast.error('Failed to add video');
    } finally {
      setVideoUploading(false);
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const addTech = () => {
    if (techInput.trim()) {
      setVideoForm(prev => ({
        ...prev,
        tech_stack: [...prev.tech_stack, techInput.trim()]
      }));
      setTechInput('');
    }
  };

  const removeTech = (index) => {
    setVideoForm(prev => ({
      ...prev,
      tech_stack: prev.tech_stack.filter((_, i) => i !== index)
    }));
  };

  // Diagram handlers
  const handleDiagramFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setDiagramForm(prev => ({ ...prev, image_data: reader.result }));
      toast.success('Image loaded');
    };
    reader.readAsDataURL(file);
  };

  const handleDiagramSubmit = async (e) => {
    e.preventDefault();
    if (!diagramForm.image_data) {
      toast.error('Please upload an image');
      return;
    }

    setDiagramUploading(true);
    try {
      const response = await axios.post(`${API}/admin/diagrams`, diagramForm);
      setDiagrams(prev => [...prev, response.data]);
      setShowDiagramForm(false);
      setDiagramForm({
        title: '',
        description: '',
        category: 'ai_platform',
        components: [],
        image_data: '',
      });
      toast.success('Diagram added successfully');
    } catch (error) {
      toast.error('Failed to add diagram');
    } finally {
      setDiagramUploading(false);
    }
  };

  const deleteDiagram = async (diagramId) => {
    try {
      await axios.delete(`${API}/admin/diagrams/${diagramId}`);
      setDiagrams(prev => prev.filter(d => d.id !== diagramId));
      toast.success('Diagram deleted');
    } catch (error) {
      toast.error('Failed to delete diagram');
    }
  };

  const addComponent = () => {
    if (componentInput.trim()) {
      setDiagramForm(prev => ({
        ...prev,
        components: [...prev.components, componentInput.trim()]
      }));
      setComponentInput('');
    }
  };

  const removeComponent = (index) => {
    setDiagramForm(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  // Capability Deck handlers
  const handleDeckUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('PDF must be under 20MB');
      return;
    }

    setDeckUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const formData = new FormData();
        formData.append('file_data', reader.result);
        formData.append('filename', file.name);

        await axios.post(`${API}/admin/capability-deck`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setCapabilityDeck({ filename: file.name, file_data: reader.result });
        toast.success('Capability deck uploaded successfully');
        setDeckUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Failed to upload capability deck');
      setDeckUploading(false);
    }
  };

  const deleteDeck = async () => {
    try {
      await axios.delete(`${API}/admin/capability-deck`);
      setCapabilityDeck(null);
      toast.success('Capability deck deleted');
    } catch (error) {
      toast.error('Failed to delete capability deck');
    }
  };

  const categoryLabels = {
    ai_platform: 'AI Platform Architecture',
    agent_orchestration: 'Agent Orchestration',
    data_architecture: 'Data Architecture',
  };

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8" data-testid="admin-console">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold">Admin Console</h1>
            <p className="text-muted-foreground">Manage your portfolio content</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="border-teal/30"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline"
              asChild
              className="border-teal/30"
            >
              <a href="/">Back to Site</a>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <Mail className="w-6 h-6 text-teal mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.contacts}</p>
                <p className="text-xs text-muted-foreground">Total Contacts</p>
              </CardContent>
            </Card>
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <Eye className="w-6 h-6 text-orange mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.unread_contacts}</p>
                <p className="text-xs text-muted-foreground">Unread</p>
              </CardContent>
            </Card>
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <Video className="w-6 h-6 text-blue mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.videos}</p>
                <p className="text-xs text-muted-foreground">Videos</p>
              </CardContent>
            </Card>
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <Layers className="w-6 h-6 text-teal mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.diagrams}</p>
                <p className="text-xs text-muted-foreground">Diagrams</p>
              </CardContent>
            </Card>
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <Building2 className="w-6 h-6 text-orange mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.programs}</p>
                <p className="text-xs text-muted-foreground">Programs</p>
              </CardContent>
            </Card>
            <Card className="glass-light border-0">
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 text-blue mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.capability_deck_uploaded ? '1' : '0'}</p>
                <p className="text-xs text-muted-foreground">Capability Deck</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="glass-light mb-6 flex-wrap h-auto p-1">
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="py-2">
              <Mail className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-tab-videos" className="py-2">
              <Video className="w-4 h-4 mr-2" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="diagrams" data-testid="admin-tab-diagrams" className="py-2">
              <Layers className="w-4 h-4 mr-2" />
              Diagrams
            </TabsTrigger>
            <TabsTrigger value="deck" data-testid="admin-tab-deck" className="py-2">
              <FileText className="w-4 h-4 mr-2" />
              Capability Deck
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="glass-light border-0">
              <CardHeader>
                <CardTitle>Contact Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No contact submissions yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((contact) => (
                          <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                            <TableCell>
                              <Badge variant={contact.read ? 'secondary' : 'default'}>
                                {contact.read ? 'Read' : 'New'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{contact.name}</TableCell>
                            <TableCell>{contact.email}</TableCell>
                            <TableCell>{contact.company || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{contact.inquiry_type}</Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{contact.message}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(contact.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {!contact.read && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => markAsRead(contact.id)}
                                    data-testid={`mark-read-${contact.id}`}
                                  >
                                    <Check className="w-4 h-4 text-teal" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteContact(contact.id)}
                                  data-testid={`delete-contact-${contact.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <Card className="glass-light border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Video Gallery</CardTitle>
                <Button 
                  onClick={() => setShowVideoForm(true)}
                  className="bg-teal hover:bg-teal-dark"
                  data-testid="add-video-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Video
                </Button>
              </CardHeader>
              <CardContent>
                {videos.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No videos added yet
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videos.map((video) => (
                      <Card key={video.id} className="glass-light border-0" data-testid={`video-item-${video.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline">{video.platform}</Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteVideo(video.id)}
                              data-testid={`delete-video-${video.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          {video.thumbnail_data && (
                            <div className="aspect-video rounded-md overflow-hidden mb-2">
                              <img src={video.thumbnail_data} alt={video.title} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <h4 className="font-semibold mb-1">{video.title}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {video.tech_stack.slice(0, 3).map((tech, idx) => (
                              <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Video Dialog */}
            <Dialog open={showVideoForm} onOpenChange={setShowVideoForm}>
              <DialogContent className="glass-light border-0 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-video-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Video</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleVideoSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={videoForm.title}
                      onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                      required
                      placeholder="Video title"
                      className="glass-light border-teal/20"
                      data-testid="video-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={videoForm.description}
                      onChange={(e) => setVideoForm(prev => ({ ...prev, description: e.target.value }))}
                      required
                      placeholder="Video description"
                      className="glass-light border-teal/20"
                      data-testid="video-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Video URL (for YouTube/Loom/Vimeo)</Label>
                      <Input
                        value={videoForm.video_url}
                        onChange={(e) => setVideoForm(prev => ({ ...prev, video_url: e.target.value }))}
                        placeholder="https://..."
                        className="glass-light border-teal/20"
                        data-testid="video-url-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Platform</Label>
                      <Select
                        value={videoForm.platform}
                        onValueChange={(value) => setVideoForm(prev => ({ ...prev, platform: value }))}
                      >
                        <SelectTrigger className="glass-light border-teal/20" data-testid="video-platform-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">YouTube</SelectItem>
                          <SelectItem value="loom">Loom</SelectItem>
                          <SelectItem value="vimeo">Vimeo</SelectItem>
                          <SelectItem value="uploaded">Uploaded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Or Upload Video File (max 100MB)</Label>
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoFileChange}
                        className="glass-light border-teal/20"
                        data-testid="video-file-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Thumbnail Image (optional)</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                        className="glass-light border-teal/20"
                        data-testid="video-thumbnail-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tech Stack</Label>
                    <div className="flex gap-2">
                      <Input
                        value={techInput}
                        onChange={(e) => setTechInput(e.target.value)}
                        placeholder="Add technology"
                        className="glass-light border-teal/20"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())}
                        data-testid="tech-stack-input"
                      />
                      <Button type="button" onClick={addTech} variant="outline" className="border-teal/30">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {videoForm.tech_stack.map((tech, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {tech}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => removeTech(idx)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowVideoForm(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-teal hover:bg-teal-dark" 
                      data-testid="save-video-button"
                      disabled={videoUploading}
                    >
                      {videoUploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Video
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Diagrams Tab */}
          <TabsContent value="diagrams">
            <Card className="glass-light border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Architecture Diagrams</CardTitle>
                <Button 
                  onClick={() => setShowDiagramForm(true)}
                  className="bg-teal hover:bg-teal-dark"
                  data-testid="add-diagram-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Diagram
                </Button>
              </CardHeader>
              <CardContent>
                {diagrams.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No architecture diagrams added yet</p>
                    <p className="text-sm mt-2">Upload diagrams to display in the Capability Explorer</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {diagrams.map((diagram) => (
                      <Card key={diagram.id} className="glass-light border-0" data-testid={`diagram-item-${diagram.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline">{categoryLabels[diagram.category] || diagram.category}</Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteDiagram(diagram.id)}
                              data-testid={`delete-diagram-${diagram.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          {diagram.image_data && (
                            <div className="aspect-video rounded-md overflow-hidden mb-2 bg-muted">
                              <img src={diagram.image_data} alt={diagram.title} className="w-full h-full object-contain" />
                            </div>
                          )}
                          <h4 className="font-semibold mb-1">{diagram.title}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{diagram.description}</p>
                          {diagram.components.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {diagram.components.slice(0, 4).map((comp, idx) => (
                                <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded">
                                  {comp}
                                </span>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Diagram Dialog */}
            <Dialog open={showDiagramForm} onOpenChange={setShowDiagramForm}>
              <DialogContent className="glass-light border-0 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-diagram-dialog">
                <DialogHeader>
                  <DialogTitle>Add Architecture Diagram</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleDiagramSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={diagramForm.title}
                      onChange={(e) => setDiagramForm(prev => ({ ...prev, title: e.target.value }))}
                      required
                      placeholder="Diagram title"
                      className="glass-light border-teal/20"
                      data-testid="diagram-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={diagramForm.description}
                      onChange={(e) => setDiagramForm(prev => ({ ...prev, description: e.target.value }))}
                      required
                      placeholder="Describe the architecture"
                      className="glass-light border-teal/20"
                      data-testid="diagram-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={diagramForm.category}
                      onValueChange={(value) => setDiagramForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="glass-light border-teal/20" data-testid="diagram-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai_platform">AI Platform Architecture</SelectItem>
                        <SelectItem value="agent_orchestration">Agent Orchestration</SelectItem>
                        <SelectItem value="data_architecture">Data Architecture</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Upload Diagram Image</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleDiagramFileChange}
                      className="glass-light border-teal/20"
                      data-testid="diagram-file-input"
                    />
                    {diagramForm.image_data && (
                      <div className="mt-2 aspect-video max-w-xs rounded-md overflow-hidden bg-muted">
                        <img src={diagramForm.image_data} alt="Preview" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Components (for layer highlighting)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={componentInput}
                        onChange={(e) => setComponentInput(e.target.value)}
                        placeholder="Add component name"
                        className="glass-light border-teal/20"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addComponent())}
                        data-testid="diagram-component-input"
                      />
                      <Button type="button" onClick={addComponent} variant="outline" className="border-teal/30">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {diagramForm.components.map((comp, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {comp}
                          <X 
                            className="w-3 h-3 cursor-pointer" 
                            onClick={() => removeComponent(idx)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowDiagramForm(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-teal hover:bg-teal-dark" 
                      data-testid="save-diagram-button"
                      disabled={diagramUploading}
                    >
                      {diagramUploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Diagram
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Capability Deck Tab */}
          <TabsContent value="deck">
            <Card className="glass-light border-0">
              <CardHeader>
                <CardTitle>Capability Deck</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-xl mx-auto text-center py-8">
                  {capabilityDeck ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-6"
                    >
                      <div className="w-20 h-20 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto">
                        <FileText className="w-10 h-10 text-teal" />
                      </div>
                      <div>
                        <h3 className="text-xl font-heading font-semibold mb-2">Capability Deck Uploaded</h3>
                        <p className="text-muted-foreground">{capabilityDeck.filename}</p>
                      </div>
                      <div className="flex gap-3 justify-center">
                        <Button
                          variant="outline"
                          className="border-teal/30"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = capabilityDeck.file_data;
                            link.download = capabilityDeck.filename;
                            link.click();
                          }}
                          data-testid="download-deck-button"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          className="border-destructive/30 text-destructive hover:bg-destructive/10"
                          onClick={deleteDeck}
                          data-testid="delete-deck-button"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-6"
                    >
                      <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto">
                        <Upload className="w-10 h-10 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="text-xl font-heading font-semibold mb-2">Upload Capability Deck</h3>
                        <p className="text-muted-foreground mb-4">
                          Upload your capability deck PDF. Visitors will be able to download it from the Contact section.
                        </p>
                      </div>
                      <div>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={handleDeckUpload}
                          className="glass-light border-teal/20 max-w-xs mx-auto"
                          data-testid="deck-file-input"
                          disabled={deckUploading}
                        />
                        {deckUploading && (
                          <div className="flex items-center justify-center gap-2 mt-4 text-teal">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Uploading...</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
